import React, { useState, useEffect } from 'react';
import {
  Box,
  Select,
  Text,
  useColorModeValue,
  Stack,
  Input,
  Flex,
  Divider,
  IconButton,
  Tooltip,
  Button,
  useToast,
  Skeleton,
  SimpleGrid,
} from '@chakra-ui/react';
import { EditIcon, DragHandleIcon, CheckIcon } from '@chakra-ui/icons';
import ApexChart from 'react-apexcharts';
import Card from 'components/card/Card';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import axiosInstance from 'api/axiosInstance';
import apiUrls from 'api/apiUrls';

const ResponsiveGridLayout = WidthProvider(Responsive);
const chartTypes = ['line', 'bar', 'area', 'scatter', 'pie', 'radialBar'];

const TelemetryChart = ({
  telemetryData,
  selectedKeysDevice,
  deviceId,
  selectedSlaveId,
}) => {
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [chartConfigs, setChartConfigs] = useState({});
  const [layout, setLayout] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [dragMode, setDragMode] = useState(false);
  const [fullChartConfig, setFullChartConfig] = useState({
    layout: [],
    chartConfigs: {},
  });
  const [loading, setLoading] = useState(true); // <- Loading state

  const toast = useToast();

  const panelBg = useColorModeValue('white', 'navy.700');
  const borderColor = useColorModeValue('navy.200', 'navy.600');
  const textColor = useColorModeValue('navy.800', 'white');

  // Load saved chart config from server on relevant prop changes
  useEffect(() => {
    const fetchConfig = async () => {
      setSelectedKeys([]);
      if (!deviceId || !selectedSlaveId || !selectedKeysDevice?.length) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const resp = await axiosInstance.get(
          apiUrls.deviceAttributes(deviceId, 'slave_chart_config'),
        );
        const fullConfig = resp.data.find(
          (item) => item.key === 'slave_chart_config',
        )?.value || {
          layout: [],
          chartConfigs: {},
        };
        setFullChartConfig(fullConfig);

        const initialChartConfigs = {};
        const initialLayout = [];

        selectedKeysDevice.forEach((param, index) => {
          const chartKey = `${selectedSlaveId}::${param}`;
          const config = fullConfig.chartConfigs?.[chartKey] || {
            type: 'area',
            color: '#7a31ce',
          };
          initialChartConfigs[chartKey] = config;

          const layoutItem = fullConfig.layout?.find(
            (l) => l.i === chartKey,
          ) || {
            i: chartKey,
            x: index % 3,
            y: Math.floor(index / 3) * 3,
            w: 1,
            h: 3,
            minW: 1,
            minH: 2,
          };
          initialLayout.push(layoutItem);
        });

        setSelectedKeys(selectedKeysDevice);
        setChartConfigs(initialChartConfigs);
        setLayout(initialLayout);
      } catch (err) {
        console.error('Failed to load chart config:', err);
        toast({ title: 'Failed to load chart config', status: 'error' });
      } finally {
        setLoading(false); // End loading
      }
    };

    fetchConfig();
  }, [deviceId, selectedKeysDevice, selectedSlaveId, toast]);

  const updateChartConfig = (chartKey, field, value) => {
    setChartConfigs((prev) => ({
      ...prev,
      [chartKey]: {
        ...prev[chartKey],
        [field]: value,
      },
    }));
  };

  const onLayoutChange = (newLayout) => {
    setLayout(newLayout);
  };

  const saveChartConfig = async () => {
    try {
      const updatedFullConfig = {
        layout: fullChartConfig.layout.filter(
          (l) => !l.i.startsWith(`${selectedSlaveId}::`),
        ),
        chartConfigs: Object.fromEntries(
          Object.entries(fullChartConfig.chartConfigs).filter(
            ([key]) => !key.startsWith(`${selectedSlaveId}::`),
          ),
        ),
      };

      updatedFullConfig.layout.push(...layout);
      updatedFullConfig.chartConfigs = {
        ...updatedFullConfig.chartConfigs,
        ...chartConfigs,
      };

      const payload = {
        slave_chart_config: updatedFullConfig,
      };

      await axiosInstance.post(apiUrls.updateDevice(deviceId), payload);
      setFullChartConfig(updatedFullConfig);
      toast({ title: 'Chart layout saved successfully', status: 'success' });
    } catch (err) {
      console.error('Save failed:', err);
      toast({
        title: 'Save failed',
        description: err.message,
        status: 'error',
      });
    }
  };

  const getChartSeries = (key, type) => {
    const seriesData = telemetryData
      .map((item) => {
        const val = item[key];
        return val !== undefined
          ? [new Date(item.timestamp).getTime(), parseFloat(val)]
          : null;
      })
      .filter(Boolean);

    switch (type) {
      case 'pie':
      case 'radialBar': {
        const lastItem = telemetryData[telemetryData.length - 1];
        const value = lastItem ? parseFloat(lastItem[key]) : 0;
        return [{ name: key, data: [value] }];
      }
      case 'scatter':
        return [
          {
            name: key,
            data: seriesData.map(([x, y]) => ({ x, y })),
          },
        ];
      default:
        return [
          {
            name: key,
            data: seriesData,
          },
        ];
    }
  };

  const getChartOptions = (label, type, color) => {
    const showGrid = editMode && type === 'line';

    const baseOptions = {
      chart: {
        type,
        zoom: { enabled: true },
        toolbar: { show: true },
        background: panelBg,
        fontFamily: 'Inter, sans-serif',
        animations: { enabled: false },
      },
      colors: [color],
      stroke: { curve: 'smooth', width: 1 },
      dataLabels: { enabled: false },
      legend: {
        position: 'top',
        labels: { colors: textColor, fontWeight: '600' },
      },
      grid: {
        borderColor: borderColor,
        strokeDashArray: showGrid ? 4 : 0,
        row: {
          colors: ['transparent'],
          opacity: showGrid ? 0.3 : 0,
        },
        show: showGrid,
      },
      tooltip: {
        x: {
          formatter: (value) =>
            new Date(value).toLocaleString(undefined, {
              hour12: false,
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            }),
        },
        theme: textColor === 'white' ? 'dark' : 'light',
      },
      title: {
        text: label.charAt(0).toUpperCase() + label.slice(1),
        align: 'left',
        style: {
          color: textColor,
          fontSize: '16px',
          fontWeight: 600,
        },
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            legend: {
              position: 'bottom',
            },
          },
        },
      ],
    };

    const datetimeAxis = {
      type: 'datetime',
      labels: {
        style: { colors: textColor, fontSize: '11px' },
        rotate: -45,
        formatter: (value) =>
          new Date(value).toLocaleString(undefined, {
            hour12: false,
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          }),
      },
      axisBorder: { color: borderColor },
      axisTicks: { color: borderColor },
    };

    if (type === 'area') {
      return {
        ...baseOptions,
        fill: {
          type: 'gradient',
          gradient: {
            type: 'vertical',
            shadeIntensity: 1,
            inverseColors: false,
            opacityFrom: 0.8,
            opacityTo: 0,
            stops: [0, 100],
            colorStops: [
              { offset: 0, color: color, opacity: 0.8 },
              { offset: 100, color: color, opacity: 0 },
            ],
          },
        },
        xaxis: datetimeAxis,
        yaxis: {
          labels: { style: { colors: textColor, fontSize: '12px' } },
          axisBorder: { color: borderColor },
          axisTicks: { color: borderColor },
        },
      };
    }

    if (['line', 'bar', 'scatter'].includes(type)) {
      return {
        ...baseOptions,
        xaxis: datetimeAxis,
        yaxis: {
          labels: { style: { colors: textColor, fontSize: '12px' } },
          axisBorder: { color: borderColor },
          axisTicks: { color: borderColor },
        },
      };
    }

    if (type === 'pie') {
      return {
        ...baseOptions,
        labels: [label],
        legend: { position: 'bottom' },
      };
    }

    if (type === 'radialBar') {
      return {
        ...baseOptions,
        plotOptions: {
          radialBar: {
            dataLabels: {
              name: { show: true },
              value: { show: true },
            },
          },
        },
      };
    }

    return baseOptions;
  };

  return (
    <Box>
      <Flex justify="space-between" mb={2} gap={2} flexWrap="wrap">
        <Flex gap={2}>
          <Tooltip label={editMode ? 'Disable Edit Mode' : 'Enable Edit Mode'}>
            <IconButton
              aria-label="Toggle edit mode"
              icon={<EditIcon />}
              onClick={() => setEditMode((prev) => !prev)}
              colorScheme={editMode ? 'purple' : 'gray'}
              variant={editMode ? 'solid' : 'outline'}
              size="sm"
            />
          </Tooltip>

          <Tooltip
            label={dragMode ? 'Disable Drag & Resize' : 'Enable Drag & Resize'}
          >
            <IconButton
              aria-label="Toggle drag/resize"
              icon={<DragHandleIcon />}
              onClick={() => setDragMode((prev) => !prev)}
              colorScheme={dragMode ? 'purple' : 'gray'}
              variant={dragMode ? 'solid' : 'outline'}
              size="sm"
            />
          </Tooltip>
        </Flex>

        <Button
          leftIcon={<CheckIcon />}
          size="sm"
          colorScheme="purple"
          onClick={saveChartConfig}
        >
          Save Layout
        </Button>
      </Flex>

      {loading ? (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {Array.from({ length: selectedKeysDevice?.length || 3 }).map(
            (_, idx) => (
              <Skeleton key={idx} height="300px" borderRadius="md" />
            ),
          )}
        </SimpleGrid>
      ) : selectedKeys.length === 0 || telemetryData.length === 0 ? (
        <Text color={textColor} textAlign="center" mt={6}>
          No data to display. Please select parameters.
        </Text>
      ) : (
        <ResponsiveGridLayout
          className="layout"
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          cols={{ lg: 3, md: 3, sm: 2, xs: 1, xxs: 1 }}
          rowHeight={150}
          margin={[24, 24]}
          isDraggable={dragMode}
          isResizable={dragMode}
          compactType={null}
          preventCollision={false}
          layout={layout}
          onLayoutChange={onLayoutChange}
        >
          {selectedKeys.map((param) => {
            const chartKey = `${selectedSlaveId}::${param}`;
            const config = chartConfigs[chartKey] || {
              type: 'line',
              color: '#7a31ce',
            };
            const { type, color } = config;

            const layoutItem = layout.find((l) => l.i === chartKey) || {
              i: chartKey,
              x: 0,
              y: Infinity,
              w: 1,
              h: 3,
            };

            return (
              <div key={chartKey} data-grid={layoutItem}>
                <Card
                  bg={panelBg}
                  transition="all 0.3s"
                  _hover={{ boxShadow: editMode ? 'lg' : 'none' }}
                  height="100%"
                  display="flex"
                  flexDirection="column"
                >
                  <Stack mb={4} spacing={3} flexShrink={0}>
                    <Flex
                      justify="space-between"
                      align="center"
                      gap={4}
                      flexWrap="wrap"
                    >
                      <Box flex="1" minW="140px">
                        <Text
                          fontSize="sm"
                          fontWeight="semibold"
                          mb={1}
                          color={textColor}
                        >
                          Chart Type
                        </Text>
                        <Select
                          value={type}
                          onChange={(e) =>
                            updateChartConfig(chartKey, 'type', e.target.value)
                          }
                          size="sm"
                          borderColor={borderColor}
                          bg={panelBg}
                          isDisabled={!editMode}
                        >
                          {chartTypes.map((t) => (
                            <option key={t} value={t}>
                              {t.charAt(0).toUpperCase() + t.slice(1)}
                            </option>
                          ))}
                        </Select>
                      </Box>

                      <Box flex="1" textAlign="end" minW="140px">
                        <Text
                          fontSize="sm"
                          fontWeight="semibold"
                          mb={1}
                          color={textColor}
                        >
                          Chart Color
                        </Text>
                        <Input
                          type="color"
                          value={color}
                          onChange={(e) =>
                            updateChartConfig(chartKey, 'color', e.target.value)
                          }
                          size="sm"
                          w="80px"
                          p={1}
                          bg="transparent"
                          isDisabled={!editMode}
                        />
                      </Box>
                    </Flex>
                    <Divider borderColor={borderColor} />
                  </Stack>

                  <Box flex="1" minHeight={0}>
                    <ApexChart
                      options={getChartOptions(param, type, color)}
                      series={getChartSeries(param, type)}
                      type={type}
                      height="100%"
                      width="100%"
                    />
                  </Box>
                </Card>
              </div>
            );
          })}
        </ResponsiveGridLayout>
      )}
    </Box>
  );
};

export default TelemetryChart;
